import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { CONTACT_DETAILS } from '../config/contactDetails';
import {useEventTracking} from "@/hooks/useEventTracking.ts";

interface ContactInfoProps {
    variant?: 'default' | 'footer' | 'inline' | 'card';
    showAddress?: boolean;
    showEmail?: boolean;
    showPhone?: boolean;
    emailType?: EmailAccessKey;
    className?: string;
}

type EmailAccessKey = 'email' | 'support' | 'legal' | 'privacy';

export const ContactInfo: React.FC<ContactInfoProps> = ({
                                                            variant = 'default',
                                                            showAddress = true,
                                                            showEmail = true,
                                                            showPhone = true,
                                                            emailType = 'email',
                                                            className = ''
                                                        }) => {

    const { trackClick } = useEventTracking();

    // Determine which email to display based on emailType
    const getEmail = (type: EmailAccessKey) => {
        switch (type) {
            case 'support':
                return CONTACT_DETAILS.support.email;
            case 'legal':
                return CONTACT_DETAILS.legal.email;
            case 'privacy':
                return CONTACT_DETAILS.privacy.email;
            case 'email':
            default:
                return CONTACT_DETAILS.email;
        }
    };

    const selectedEmail = getEmail(emailType);

    const baseClasses = {
        default: 'space-y-4',
        footer: 'space-y-2',
        inline: 'flex flex-wrap gap-4',
        card: 'space-y-6'
    };

    const iconClasses = {
        default: 'h-6 w-6 text-primary-600 mr-4 mt-1',
        footer: 'h-4 w-4 mr-2',
        inline: 'h-4 w-4 mr-2',
        card: 'h-6 w-6 text-primary-600 mr-4 mt-1'
    };

    const textClasses = {
        default: 'text-gray-600',
        footer: 'text-gray-300',
        inline: 'text-gray-600',
        card: 'text-gray-600'
    };

    const titleClasses = {
        default: 'font-semibold text-gray-900',
        footer: 'font-medium text-gray-200',
        inline: 'font-medium text-gray-900',
        card: 'font-semibold text-gray-900'
    };

    const ItemWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
        if (variant === 'inline') {
            return <div className="flex items-center">{children}</div>;
        }
        return <div className="flex items-start">{children}</div>;
    };

    return (
        <div className={`${baseClasses[variant]} ${className}`}>
            {showEmail && (
                <ItemWrapper>
                    <Mail className={iconClasses[variant]} />
                    <div>
                        {variant !== 'inline' && (
                            <h3 className={titleClasses[variant]}>Email</h3>
                        )}
                        <p className={textClasses[variant]}>
                            <a
                                href={`mailto:${selectedEmail}`}
                                onClick={() => trackClick('email-contact', 'Email Contact', 'link')}
                                className="hover:text-primary-600 transition-colors"
                            >
                                {selectedEmail}
                            </a>
                        </p>
                    </div>
                </ItemWrapper>
            )}

            {showPhone && (
                <ItemWrapper>
                    <Phone className={iconClasses[variant]} />
                    <div>
                        {variant !== 'inline' && (
                            <h3 className={titleClasses[variant]}>Phone</h3>
                        )}
                        <p className={textClasses[variant]}>
                            <a
                                href={`tel:${CONTACT_DETAILS.phone}`}
                                className="hover:text-primary-600 transition-colors"
                            >
                                {CONTACT_DETAILS.phone}
                            </a>
                        </p>
                    </div>
                </ItemWrapper>
            )}

            {showAddress && (
                <ItemWrapper>
                    <MapPin className={iconClasses[variant]} />
                    <div>
                        {variant !== 'inline' && (
                            <h3 className={titleClasses[variant]}>Office</h3>
                        )}
                        <p className={textClasses[variant]}>
                            {variant === 'inline' ? (
                                CONTACT_DETAILS.address.full
                            ) : (
                                <>
                                    {CONTACT_DETAILS.address.street}<br />
                                    {CONTACT_DETAILS.address.city}, {CONTACT_DETAILS.address.state} {CONTACT_DETAILS.address.zip}
                                </>
                            )}
                        </p>
                    </div>
                </ItemWrapper>
            )}
        </div>
    );
};
